import { useEffect, useMemo, useState } from 'react';
import {
  applyAction,
  legalMoves,
  lookaheadPolicy,
  material,
  newGame,
  STANDARD_RULES,
  type Action,
  type D20Face,
  type GameState,
  type Player,
  type Ruleset,
  type SaveFace,
} from '@rookandroll/engine';
import { Board } from './Board';
import { Die, SaveDie } from './Dice';
import { describe } from './log';

const bot = lookaheadPolicy();
const randomSeed = () => (Math.random() * 2 ** 32) >>> 0;

interface View {
  game: GameState;
  log: string[];
  face: D20Face | null;
  save: SaveFace | null;
  rollKey: number;
  saveKey: number;
  last: { from: number | null; to: number } | null;
  resigned: boolean;
}

const start = (rules: Ruleset, human: Player): View => ({
  game: newGame(randomSeed(), rules),
  log: [human === 'A' ? 'New game. You play the light pieces and move first.' : 'New game. You play the dark pieces and move second; the bot rolls first.'],
  face: null,
  save: null,
  rollKey: 0,
  saveKey: 0,
  last: null,
  resigned: false,
});

function step(v: View, action: Action, human: Player): View {
  const r = applyAction(v.game, action);
  let { face, save, rollKey, saveKey, last } = v;
  for (const e of r.events) {
    if (e.type === 'rolled') {
      face = e.face;
      save = null;
      rollKey++;
    } else if (e.type === 'save-rolled') {
      save = e.face;
      saveKey++;
    } else if (e.type === 'moved') last = { from: e.move.kind === 'move' ? e.move.from : null, to: e.move.to };
  }
  const lines = describe(r.events, v.game, human).reverse();
  return { ...v, game: r.state, log: [...lines, ...v.log].slice(0, 40), face, save, rollKey, saveKey, last };
}

const COLOUR = { l: 'light', d: 'dark' } as const;

export function App() {
  const [opts, setOpts] = useState({ knightHeavy: false, pawnRebuild: false, playFirst: true });
  const [human, setHuman] = useState<Player>('A');
  const [view, setView] = useState<View>(() => start(STANDARD_RULES, 'A'));
  const [sel, setSel] = useState<number | null>(null);
  const g = view.game;

  const act = (a: Action) => setView((v) => step(v, a, human));
  const ended = g.phase === 'over' || view.resigned;
  const newMatch = () => {
    if (!ended && g.rollIndex > 0 && !window.confirm('Abandon the current game and start a new one?')) return;
    const side: Player = opts.playFirst ? 'A' : 'B';
    setHuman(side);
    setView(start({ ...STANDARD_RULES, knightHeavy: opts.knightHeavy, pawnRebuild: opts.pawnRebuild }, side));
  };
  const resign = () => {
    if (ended || !window.confirm('Resign this game? The bot will win.')) return;
    setView((v) => ({ ...v, resigned: true, log: ['You resigned. The bot wins.', ...v.log].slice(0, 40) }));
  };

  useEffect(() => setSel(null), [g]);

  // the bot acts whenever the decision is its own
  useEffect(() => {
    if (g.phase === 'over' || view.resigned) return;
    const actor = g.phase === 'save' ? g.hurt! : g.turn;
    if (actor === human) return;
    const delay = g.phase === 'move' ? 650 : g.phase === 'save' ? 1100 : 900;
    const id = setTimeout(() => {
      if (g.phase === 'roll') act({ type: 'roll' });
      else if (g.phase === 'save') act({ type: bot.reverse(g) === 'veto' ? 'veto' : 'save' });
      else act({ type: 'play', move: bot.move(g, legalMoves(g), (n) => Math.floor(Math.random() * n)) });
    }, delay);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g, human, view.resigned]);

  const myTurn = g.phase === 'move' && g.turn === human && !view.resigned;
  const moves = useMemo(() => (myTurn ? legalMoves(g) : []), [g, myTurn]);
  const movable = useMemo(() => new Set(moves.flatMap((m) => (m.kind === 'move' ? [m.from] : []))), [moves]);
  const targets = useMemo(
    () => new Set(moves.flatMap((m) => (sel === -1 ? (m.kind === 'add' ? [m.to] : []) : m.kind === 'move' && m.from === sel ? [m.to] : []))),
    [moves, sel],
  );
  const canAdd = moves.some((m) => m.kind === 'add');
  const botName = human === 'A' ? 'B' : 'A';

  const onSquare = (i: number) => {
    if (!myTurn) return;
    if (sel !== null) {
      const m = moves.find((x) => (sel === -1 ? x.kind === 'add' && x.to === i : x.kind === 'move' && x.from === sel && x.to === i));
      if (m) {
        act({ type: 'play', move: m });
        return;
      }
    }
    setSel(movable.has(i) ? i : null);
  };

  const humanHurt = g.phase === 'save' && g.hurt === human && !view.resigned;
  const botThinking = !ended && (g.phase === 'save' ? g.hurt! : g.turn) !== human;
  const mine = COLOUR[g.control[human]];
  const heavyNames = g.rules.knightHeavy ? 'rook, bishop, queen, knight' : 'rook, bishop, queen';

  let status: string;
  if (view.resigned) status = 'You resigned. The bot wins.';
  else if (g.phase === 'over') status = g.winner === human ? 'You captured the king. You win!' : 'The bot captured your king.';
  else if (humanHurt) status = 'The reverse would hurt you (you are ahead on material). Roll the save die, or spend your veto to cancel it for certain.';
  else if (botThinking) status = 'The bot is thinking…';
  else if (g.phase === 'roll') status = `Your turn. You move the ${mine} pieces. Roll the d20.`;
  else if (sel === -1) status = 'Choose an empty square on your pawn start rank.';
  else if (sel !== null) status = 'Choose a highlighted square.';
  else status = `Moves left: ${g.movesLeft}. Pick a piece to move.${canAdd ? ' You have no pawns, so you may add one instead of a move.' : ''}`;

  const who = (p: Player, name: string) => (
    <div className={`who${!ended && (g.phase === 'save' ? g.hurt === p : g.turn === p) ? ' active' : ''}`}>
      <div className="chip">
        <span className={`dot ${g.control[p]}`} />
        {name} ({COLOUR[g.control[p]]})
      </div>
      <div className="clock">
        <span className={`vt${g.vetoes[p] < 1 ? ' used' : ''}`} title="Once per game: cancel a reverse that would hurt you, for certain, instead of rolling the save die">veto {g.vetoes[p] < 1 ? 'used' : 'ready'}</span>
        {material(g.board, g.control[p])} pts
      </div>
    </div>
  );

  const total = Object.values(g.rules.faces).reduce((a, b) => a + b, 0);

  return (
    <div className="wrap">
      <header>
        <span className="tag">Demo · play the bot</span>
        <h1>Rook &amp; Roll</h1>
        <p className="sub">Chess with a d20. The roll is how many moves you get. Capture the king to win. Placeholder art; the rules are the real engine.</p>
      </header>

      <section aria-labelledby="play">
        <h2 id="play" hidden>Play</h2>
        <div className="table">
          <div className="panel board-box">
            <Board board={g.board} movable={movable} targets={targets} selected={sel} last={view.last} onSquare={onSquare} flipped={g.control[human] === 'd'} />
          </div>
          <div className="side">
            <div className="panel pad">
              {who(botName, 'Bot')}
              <div className="dieArea">
                <figure>
                  <div key={view.rollKey} className={view.rollKey ? 'roll' : ''}>
                    <Die face={view.face} />
                  </div>
                  d20
                </figure>
                <figure className={g.phase === 'save' || view.save ? '' : 'dim'}>
                  <div key={view.saveKey} className={view.saveKey ? 'roll' : ''}>
                    <SaveDie face={view.save} />
                  </div>
                  Save die
                </figure>
              </div>
              {who(human, 'You')}
              <div className="odds" aria-label="Odds">
                {(['1', '2', '3', 'skip', 'reverse'] as const).map((k) => (
                  <span key={k}>
                    {g.rules.faces[k]}× {k === 'reverse' ? 'rev' : k} {Math.round((g.rules.faces[k] / total) * 100)}%
                  </span>
                ))}
              </div>
            </div>
            <div className="panel pad">
              <div className="status" aria-live="polite">{status}</div>
              {g.phase === 'move' && g.turn === human && (
                <div className="counts">
                  <span>moves left {g.movesLeft}</span>
                  <span className={g.heavyUsed ? '' : 'on'}>heavy move {g.heavyUsed ? 'used' : 'available'}</span>
                </div>
              )}
              {ended && <div className={`banner ${g.winner === human && !view.resigned ? 'win' : 'lose'}`}>{g.winner === human && !view.resigned ? 'You win!' : 'You lose.'}</div>}
              <div className="row">
                <button className="primary" disabled={!(g.phase === 'roll' && g.turn === human && !view.resigned)} onClick={() => act({ type: 'roll' })}>
                  Roll d20
                </button>
                {humanHurt && <button className="primary" onClick={() => act({ type: 'save' })}>Roll save die</button>}
                {humanHurt && g.vetoes[human] > 0 && <button onClick={() => act({ type: 'veto' })}>Use veto</button>}
                {canAdd && <button onClick={() => setSel(-1)}>Add a pawn</button>}
                <button onClick={resign} disabled={ended}>Resign</button>
                <button onClick={newMatch}>New game</button>
              </div>
              <ul className="log" aria-live="polite" aria-label="Game log">
                {view.log.map((l, i) => (
                  <li key={`${view.log.length}-${i}`}>{l}</li>
                ))}
              </ul>
            </div>
            <div className="panel pad opts">
              <strong>Next game</strong>
              <label>
                <input type="checkbox" checked={opts.knightHeavy} onChange={(e) => setOpts({ ...opts, knightHeavy: e.target.checked })} />
                The knight is a heavy piece (once per turn, like rook, bishop and queen)
              </label>
              <label>
                <input type="checkbox" checked={opts.pawnRebuild} onChange={(e) => setOpts({ ...opts, pawnRebuild: e.target.checked })} />
                With no pawns left, a 2 or 3 lets you add a pawn instead of a move
              </label>
              <label>
                You move
                <select value={opts.playFirst ? 'first' : 'second'} onChange={(e) => setOpts({ ...opts, playFirst: e.target.value === 'first' })}>
                  <option value="first">first</option>
                  <option value="second">second</option>
                </select>
              </label>
              <span className="sub">This game: knight {g.rules.knightHeavy ? 'heavy' : 'free'}, pawn rebuild {g.rules.pawnRebuild ? 'on' : 'off'}.</span>
            </div>
          </div>
        </div>
      </section>

      <details className="rules panel pad">
        <summary>How it works</summary>
        <h3>Your turn</h3>
        <ul>
          <li>Roll the d20. A 1, 2 or 3 is how many moves you get this turn. Each is a normal chess move, by the same piece or different ones, and you must use every move you can.</li>
          <li>Heavy pieces ({heavyNames}) may move once per turn. Everything else can use every move, even the same piece again.</li>
          <li>Miss a go passes the turn. There is no check: capture the king to win. A king that moved this turn can't end the turn on an attacked square, though it may step into attack if a later move this turn gets it out.</li>
        </ul>
        <h3>Reverse: the switch</h3>
        <ul>
          <li>A reverse swaps the two armies. You and the bot trade colours and every piece changes hands, the king included. The board turns 180° so your pieces are always at the bottom, and pawns keep heading the way they were going.</li>
          <li>The reverse uses up the roll. After it the other player moves next, with their new pieces.</li>
          <li>It hurts whoever is ahead on material (pawn 1, knight 3, bishop 3, rook 5, queen 9), because they would swap their bigger army for the smaller one. So the player ahead gets two ways to stop it. If material is level nobody is hurt and the reverse just happens.</li>
        </ul>
        <h3>Save die and veto</h3>
        <ul>
          <li><b>Why it exists:</b> a reverse punishes you for being ahead, because you would be handed the smaller army. The save die is the counter: it gives the player who is ahead a chance to protect their lead instead of losing it to luck. Without it, the better position would be the riskier one to hold.</li>
          <li><b>Save die:</b> a six-sided die with 2 save faces and 4 blanks, so a 1 in 3 chance. A save cancels the reverse and a blank lets it through. It only comes out when a reverse would hurt the player ahead. It isn't rolled if material is level or if you are behind (a reverse then helps you).</li>
          <li><b>Veto:</b> each player has one veto for the whole game. Instead of rolling the save die you can spend it to cancel the reverse for certain, a guaranteed version of the same protection. The "veto ready" or "veto used" tag by each name shows who still has theirs. A used veto is gone, so save it for when a reverse would really cost you.</li>
          <li>Either way, a cancelled reverse changes nothing: no swap, and the turn passes to the other player.</li>
        </ul>
      </details>
    </div>
  );
}
